/* KHLA — Variant Media Filter (Horizon SAFE, UNIVERSAL, multi-options, QuickAdd-ready) ✅ UPDATED
   - NO rebuild, NO remove, NO clone of Horizon custom elements
   - Keeps: generic media (alt without '#') + matching option tags
   - Supports:
       - Universal: #option_<optionHandle>_<value>
         e.g. #option_color_black, #option_frame_blue, #option_lens_noir
       - (Optional legacy kept): #couleur_<value> (default handle "couleur")
   - Multi-options: media can require several tags (AND logic)
   - Option A safe: if alt has tags but none recognized, keep it
   - Fixes navigation: skips hidden slides (patch select + post-click correction)
   - 🔥 Stability upgrade: prevents "random last image" after variant change by force-selecting a preferred visible slide
   - Hides thumbs + dots + zoom thumbs + zoom media
   - Perf: runs only when selection signature changes (per gallery instance)
   - Works on PDP + Quick Add (modal) via khla:galleryReplaced
*/
(() => {
  const HIDE = 'khla-hidden';

  // ----- TAGS -----
  // Universal: #option_<handle>_<value>
  const TAG_OPTION_RE = /#option_([a-z0-9_-]+)_([^#]+)/ig;

  // Legacy (optional): #couleur_<value>
  const TAG_COLOR_RE = /#couleur_([^#]+)/ig;
  const LEGACY_COLOR_HANDLE = 'couleur';

  // ----- helpers -----
  const canon = (s) => (s || '').toString().trim().toLowerCase();
  const stripDiacritics = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const canonKey = (k) => stripDiacritics(canon(k)).replace(/[^\w]+/g, '');     // option handle
  const canonValue = (v) => stripDiacritics(canon(v)).replace(/[\s_-]+/g, '');  // option value
  const qsa = (root, sel) => Array.from(root.querySelectorAll(sel));

  function normalizeOptionName(raw){
    // "Couleur", "couleur", "COULEUR", "couleur - Horizon", etc.
    let k = stripDiacritics(canon(raw)).replace(/\s+/g,'');
    const dash = k.indexOf('-');
    const dbl  = k.indexOf('__');
    if (dash > 0) k = k.slice(0, dash);
    if (dbl  > 0) k = k.split('__')[0];
    return canonKey(k);
  }

  function ensureStyleOnce(){
    if (document.getElementById('khla-variant-media-style')) return;
    const style = document.createElement('style');
    style.id = 'khla-variant-media-style';
    style.textContent = `
      .${HIDE}{display:none!important}
      slideshow-slide.${HIDE}{display:none!important}
      .slideshow-controls__dots > li.${HIDE}{display:none!important}
      .slideshow-controls__thumbnails .slideshow-control.${HIDE}{display:none!important}
      zoom-dialog .dialog-thumbnails-list__thumbnail.${HIDE}{display:none!important}
      zoom-dialog ul.dialog-zoomed-gallery > li.${HIDE}{display:none!important}
    `;
    document.head.appendChild(style);
  }

  function getAltFrom(node){
    const img = node?.querySelector?.('img[alt], .deferred-media__poster-image[alt]');
    return (img?.getAttribute('alt') || '').trim();
  }

  function isGenericAlt(alt){
    return !(alt || '').includes('#');
  }

  function getMediaIdFromSlide(slide){
    const media = slide.querySelector?.('.product-media[data-media-id]');
    return media?.getAttribute('data-media-id') || '';
  }

  // ----- Parse required tags from alt -----
  // Returns an array of { handle, value } requirements
  function parseRequirementsFromAlt(alt){
    const reqs = [];
    if (!alt) return reqs;

    // Universal tags
    TAG_OPTION_RE.lastIndex = 0;
    let m;
    while ((m = TAG_OPTION_RE.exec(alt)) !== null){
      const handle = canonKey(m[1]);
      const value = canonValue(m[2]);
      if (handle && value) reqs.push({ handle, value });
    }

    // Legacy color tags (optional)
    TAG_COLOR_RE.lastIndex = 0;
    while ((m = TAG_COLOR_RE.exec(alt)) !== null){
      const value = canonValue(m[1]);
      if (value) reqs.push({ handle: LEGACY_COLOR_HANDLE, value });
    }

    return reqs;
  }

  // ----- Selected options (multi) -----
  // Build a map: handle -> value for all selected options in the root
  function getSelectedOptionsFromUI(root){
    const out = new Map();

    // radios
    for (const r of qsa(root, 'input[type="radio"][value]:checked')){
      const wrap = r.closest('[data-option-name]') || r.closest('fieldset,label,.product-form__input,.variant-picker,.product-variant-picker');
      const name = normalizeOptionName(
        wrap?.getAttribute('data-option-name') ||
        wrap?.querySelector('legend,label')?.textContent ||
        r.name || ''
      );
      if (!name) continue;
      const val = canonValue(r.getAttribute('aria-label') || r.value || '');
      if (val) out.set(name, val);
    }

    // selects
    for (const sel of qsa(root, 'select')){
      const wrap = sel.closest('[data-option-name]') || sel.closest('fieldset,label,.product-form__input,.variant-picker,.product-variant-picker');
      const name = normalizeOptionName(
        wrap?.getAttribute('data-option-name') ||
        sel.getAttribute('aria-label') ||
        sel.name || ''
      );
      if (!name) continue;
      const val = canonValue(sel.value || '');
      if (val) out.set(name, val);
    }

    return out;
  }

  function getSelectedOptionsFromProductJson(root){
    const out = new Map();

    const variantInput = root.querySelector('input[name="id"]');
    if (!variantInput?.value) return out;

    const jsonEl =
      root.querySelector('[data-product-json]') ||
      document.querySelector('[data-product-json]');
    if (!jsonEl?.textContent) return out;

    try {
      const product = JSON.parse(jsonEl.textContent);
      const variant = product?.variants?.find(v => String(v.id) === String(variantInput.value));
      if (!variant) return out;

      const optionNames = (product?.options || []).map(o => normalizeOptionName(o?.name || ''));
      (variant.options || []).forEach((rawVal, idx) => {
        const handle = optionNames[idx];
        const val = canonValue(rawVal || '');
        if (handle && val) out.set(handle, val);
      });
    } catch {}

    return out;
  }

  function getSelectedOptions(root){
    const ui = getSelectedOptionsFromUI(root);
    if (ui.size) return ui;

    const json = getSelectedOptionsFromProductJson(root);
    return json;
  }

  function selectionSignature(selectedMap){
    // stable key for caching: handle=value|handle=value...
    return Array.from(selectedMap.entries())
      .sort(([a],[b]) => a.localeCompare(b))
      .map(([h,v]) => `${h}=${v}`)
      .join('|');
  }

  // ----- Rule: should keep this media? -----
  function shouldKeepAltForSelection(alt, selectedMap){
    if (!alt) return true;
    if (isGenericAlt(alt)) return true;

    const reqs = parseRequirementsFromAlt(alt);

    // Option A safe:
    // if alt has tags (#something) but no recognized requirements, keep it.
    if ((alt.includes('#')) && reqs.length === 0) return true;

    // AND logic: all requirements must match selection, strict
    for (const { handle, value } of reqs){
      const sel = selectedMap.get(handle);
      if (!sel) return false;
      if (sel !== value) return false;
    }

    return true;
  }

  // ----- Build keepIds -----
  function buildActiveMediaIdSet(slideshow, selectedMap){
    const slides = qsa(slideshow, 'slideshow-slide');
    const keepIds = new Set();

    for (const slide of slides){
      const mid = getMediaIdFromSlide(slide);
      if (!mid) continue;

      const alt = getAltFrom(slide);
      if (shouldKeepAltForSelection(alt, selectedMap)) keepIds.add(mid);
    }

    // fallback: if nothing matched, keep all
    if (!keepIds.size){
      for (const slide of slides){
        const mid = getMediaIdFromSlide(slide);
        if (mid) keepIds.add(mid);
      }
    }

    return keepIds;
  }

  // ----- Apply hide/show -----
  function applyToSlideshow(mediaGallery, keepIds){
    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!slideshow) return null;

    const slides = qsa(slideshow, 'slideshow-slide');

    slides.forEach(slide => {
      const mid = getMediaIdFromSlide(slide);
      const show = !mid ? true : keepIds.has(mid);
      slide.classList.toggle(HIDE, !show);
    });

    const thumbsContainer =
      slideshow.querySelector('.slideshow-controls__thumbnails') ||
      slideshow.querySelector('slideshow-controls .slideshow-controls__thumbnails');

    if (thumbsContainer){
      const btns = qsa(thumbsContainer, '.slideshow-control');
      btns.forEach((btn, i) => {
        const slide = slides[i];
        const mid = slide ? getMediaIdFromSlide(slide) : '';
        const show = !mid ? true : keepIds.has(mid);
        btn.classList.toggle(HIDE, !show);
      });
    }

    const dotsOl = slideshow.querySelector('slideshow-controls .slideshow-controls__dots');
    if (dotsOl){
      const lis = qsa(dotsOl, ':scope > li');
      lis.forEach((li, i) => {
        const slide = slides[i];
        const mid = slide ? getMediaIdFromSlide(slide) : '';
        const show = !mid ? true : keepIds.has(mid);
        li.classList.toggle(HIDE, !show);
      });
    }

    return slideshow;
  }

  function applyToZoom(mediaGallery, keepIds){
    const zoom = mediaGallery.querySelector('zoom-dialog');
    if (!zoom) return;

    const lis = qsa(zoom, 'ul.dialog-zoomed-gallery > li');
    const thumbs = qsa(zoom, '.dialog-thumbnails-list__thumbnail');

    lis.forEach(li => {
      const media = li.querySelector?.('.product-media[data-media-id]');
      const mid = media?.getAttribute('data-media-id') || '';
      const show = !mid ? true : keepIds.has(mid);
      li.classList.toggle(HIDE, !show);
    });

    thumbs.forEach((b, i) => {
      const li = lis[i];
      const media = li?.querySelector?.('.product-media[data-media-id]');
      const mid = media?.getAttribute('data-media-id') || '';
      const show = !mid ? true : keepIds.has(mid);
      b.classList.toggle(HIDE, !show);
    });
  }

  // ----- Navigation fix -----
  function firstVisibleIndex(slideshow){
    const slides = qsa(slideshow, 'slideshow-slide');
    const idx = slides.findIndex(s => !s.classList.contains(HIDE));
    return idx >= 0 ? idx : 0;
  }

  // ✅ robust active index detection (prevents random jumps)
  function currentIndex(slideshow){
    const slides = qsa(slideshow, 'slideshow-slide');
    if (!slides.length) return 0;

    let idx = slides.findIndex(s => s.getAttribute('aria-hidden') === 'false');
    if (idx >= 0) return idx;

    idx = slides.findIndex(s => s.getAttribute('aria-current') === 'true');
    if (idx >= 0) return idx;

    idx = slides.findIndex(s =>
      s.classList.contains('is-selected') ||
      s.classList.contains('is-active') ||
      s.classList.contains('active')
    );
    if (idx >= 0) return idx;

    return 0;
  }

  // ✅ predictable: if active is hidden, force FIRST visible (not “nearest”, which can land on last)
  function fixIfHidden(slideshow){
    const slides = qsa(slideshow, 'slideshow-slide');
    if (!slides.length) return;

    const first = firstVisibleIndex(slideshow);
    const cur = currentIndex(slideshow);

    if (!slides[cur] || slides[cur].classList.contains(HIDE)){
      try { slideshow.select(first, undefined, { animate:false }); } catch {}
      return;
    }
  }

  function patchSelect(slideshow){
    if (!slideshow || slideshow.__khlaSelectPatched) return;
    slideshow.__khlaSelectPatched = true;

    const original = slideshow.select?.bind(slideshow);
    if (typeof original !== 'function') return;

    slideshow.select = function(index, direction, options){
      const slides = qsa(slideshow, 'slideshow-slide');
      if (!slides.length) return original(index, direction, options);

      if (slides[index] && slides[index].classList.contains(HIDE)){
        const dir = direction === 'previous' ? -1 : 1;
        let newIndex = index;
        for (let i = 0; i < slides.length; i++){
          newIndex = (newIndex + dir + slides.length) % slides.length;
          if (!slides[newIndex].classList.contains(HIDE)){
            index = newIndex;
            break;
          }
        }
      }

      const res = original(index, direction, options);
      requestAnimationFrame(() => fixIfHidden(slideshow));
      return res;
    };
  }

  function wireNavFix(mediaGallery, slideshow){
    if (!mediaGallery || !slideshow) return;
    if (mediaGallery.__khlaNavWired) return;
    mediaGallery.__khlaNavWired = true;

    patchSelect(slideshow);

    mediaGallery.addEventListener('click', () => {
      requestAnimationFrame(() => fixIfHidden(slideshow));
    }, true);

    slideshow.addEventListener?.('slideshow:change', () => fixIfHidden(slideshow));
  }

  // ----- 🔥 Stability upgrade: prefer a "true matching" tagged image, then fallback to first visible -----
  function requirementsMatch(reqs, selectedMap){
    if (!reqs || !reqs.length) return false;
    for (const { handle, value } of reqs){
      const sel = selectedMap.get(handle);
      if (!sel || sel !== value) return false;
    }
    return true;
  }

  function preferredIndex(slideshow, selectedMap){
    const slides = qsa(slideshow, 'slideshow-slide');

    // Priority: first visible slide that is tagged AND matches all requirements
    for (let i = 0; i < slides.length; i++){
      const slide = slides[i];
      if (slide.classList.contains(HIDE)) continue;

      const alt = getAltFrom(slide);
      if (!alt || isGenericAlt(alt)) continue;

      const reqs = parseRequirementsFromAlt(alt);
      if (requirementsMatch(reqs, selectedMap)) return i;
    }

    // Fallback: first visible slide
    return firstVisibleIndex(slideshow);
  }

  function forceSelectPreferred(slideshow, selectedMap){
    if (!slideshow?.select) return;

    const idx = preferredIndex(slideshow, selectedMap);

    const doSelect = () => {
      try { slideshow.select(idx, undefined, { animate:false }); } catch {}
      requestAnimationFrame(() => fixIfHidden(slideshow));
    };

    // Win race conditions: apply multiple times across frames
    doSelect();
    requestAnimationFrame(doSelect);
    requestAnimationFrame(() => requestAnimationFrame(doSelect));
    setTimeout(doSelect, 0);
  }

  // ----- Apply one gallery -----
  function applyOne(root, mediaGallery){
    ensureStyleOnce();

    const selectedMap = getSelectedOptions(root);
    if (!selectedMap || selectedMap.size === 0) return;

    const slideshow = mediaGallery.querySelector('slideshow-component');
    if (!slideshow) return;

    // reset cache if slideshow instance replaced
    if (mediaGallery.__khlaLastSlideshow !== slideshow){
      mediaGallery.__khlaLastSlideshow = slideshow;
      mediaGallery.__khlaPrevSig = '';
    }

    const sig = selectionSignature(selectedMap);

    // perf: only if selection changed
    const prevSig = mediaGallery.__khlaPrevSig || '';
    if (sig === prevSig) return;
    mediaGallery.__khlaPrevSig = sig;

    const keepIds = buildActiveMediaIdSet(slideshow, selectedMap);

    const ss = applyToSlideshow(mediaGallery, keepIds);
    applyToZoom(mediaGallery, keepIds);

    if (ss){
      wireNavFix(mediaGallery, ss);

      // 🔥 stable: always land on a preferred visible slide
      forceSelectPreferred(ss, selectedMap);
    }
  }

  function applyRoot(root){
    const galleries = qsa(root, 'media-gallery');
    if (!galleries.length) return;
    galleries.forEach(mg => applyOne(root, mg));
  }

  // ----- Wiring -----
  function wireRoot(root){
    if (!root || root.__khlaWired) return;
    root.__khlaWired = true;

    const run = () => requestAnimationFrame(() => applyRoot(root));

    [60, 140, 260, 520].forEach(d => setTimeout(run, d));

    root.addEventListener('change', (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.matches('input[type="radio"], select')) return;
      run();
    });

    root.addEventListener('variant:change', run);

    const mo = new MutationObserver((list) => {
      if (list.some(m => Array.from(m.addedNodes).some(n => n.nodeType === 1 && (n.matches?.('media-gallery') || n.querySelector?.('media-gallery'))))) {
        run();
      }
    });
    mo.observe(root, { childList:true, subtree:true });
  }

  function initPDP(){
    document.querySelectorAll('.product-information.section, .product-information').forEach(wireRoot);
  }

  function initQuickAdd(){
    const qa = document.getElementById('quick-add-modal-content');
    if (!qa) return;

    wireRoot(qa);

    requestAnimationFrame(() => {
      // force re-apply even if selection signature matches previous open
      qsa(qa, 'media-gallery').forEach(mg => { try { delete mg.__khlaPrevSig; } catch {} });
      applyRoot(qa);
    });
  }

  function init(){
    initPDP();
    initQuickAdd();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);

  document.addEventListener('khla:galleryReplaced', () => {
    initQuickAdd();
  });


    // ---------------------------
  // ✅ Quick Add: force apply on EVERY open (fix "all media visible after reopen")
  // ---------------------------
  function forceApplyQuickAddOnOpen(){
    const dialog = document.querySelector('quick-add-dialog dialog.quick-add-modal');
    const qa = document.getElementById('quick-add-modal-content');
    if (!dialog || !qa) return;

    const hardRun = () => {
      // force re-apply even if selection signature matches previous
      qsa(qa, 'media-gallery').forEach(mg => { try { delete mg.__khlaPrevSig; } catch {} });
      applyRoot(qa);
    };

    const onOpen = () => {
      // plusieurs passes, car Horizon coche souvent les options APRES injection
      [0, 40, 90, 160, 260, 420].forEach(d => setTimeout(hardRun, d));
    };

    // observe uniquement open/close
    const mo = new MutationObserver(() => {
      if (dialog.hasAttribute('open')) onOpen();
    });

    mo.observe(dialog, { attributes: true, attributeFilter: ['open'] });

    // première fois si déjà open
    if (dialog.hasAttribute('open')) onOpen();
  }

  // lance le hook
  forceApplyQuickAddOnOpen();
})();